import type { MouseEventHandler, ReactNode } from 'react';

export interface StickyCTAProps {
  children: ReactNode;
  onClick?: MouseEventHandler<HTMLButtonElement>;
  disabled?: boolean;
  sticky?: boolean;
  tone?: 'accent' | 'danger';
  type?: 'button' | 'submit';
}

/**
 * Full-width bottom CTA. Padded with `env(safe-area-inset-bottom)` so
 * it clears the iOS home indicator; backed by a fade-to-bg gradient so
 * scrollable content reads naturally behind it. Press scale uses the
 * theme's `--evi-motion-press` curve.
 * Set sticky=false on long summaries so the action follows their content.
 */
export function StickyCTA({
  children,
  onClick,
  disabled,
  sticky = true,
  tone = 'accent',
  type = 'button',
}: StickyCTAProps) {
  const bg = tone === 'danger' ? 'var(--evi-warn)' : 'var(--evi-accent)';
  return (
    <div
      style={{
        position: sticky ? 'sticky' : 'static',
        // Clear the floating pill footer: the pill (--evi-floating-height) +
        // its 14px gap and the iOS home-indicator inset (--evi-floating-bottom)
        // + 16px breathing room.
        // The trailing --evi-live-session-bar-offset addend (72px while
        // MiniChargingBar is mounted, else 0) lifts every sticky CTA
        // clear of the bar: the bar is a full-width z-49 button in the
        // same bottom band as this z-10 CTA, so without the lift an
        // active session turns any page's primary CTA into a decoy.
        bottom: sticky ? 'calc(var(--evi-floating-height) + var(--evi-floating-bottom) + 16px + var(--evi-live-session-bar-offset, 0px))' : undefined,
        left: 0,
        right: 0,
        padding: '12px 16px 4px',
        background: 'linear-gradient(to top, var(--evi-bg) 60%, transparent)',
        zIndex: 10,
      }}
    >
      <button
        type={type}
        onClick={onClick}
        disabled={disabled}
        onPointerDown={(e) => {
          if (!disabled) (e.currentTarget as HTMLElement).style.transform = 'scale(0.98)';
        }}
        onPointerUp={(e) => {
          (e.currentTarget as HTMLElement).style.transform = 'scale(1)';
        }}
        onPointerLeave={(e) => {
          (e.currentTarget as HTMLElement).style.transform = 'scale(1)';
        }}
        style={{
          width: '100%',
          height: 56,
          borderRadius: 16,
          background: disabled ? 'var(--evi-surface-2)' : bg,
          // Foreground reads `--evi-on-accent` (post-greyscale-rework
          // contract addition — see packages/skins/src/contract.ts's
          // `onAccent` field). Fallback #FFFFFF preserves the
          // long-standing white-on-green CTA behaviour for the
          // Default / Luce / Neon / Obsidian / Cartographic families
          // (all of which set onAccent: '#FFFFFF') and is the safe
          // value if the CSS var hasn't been emitted yet (early SSR,
          // tests). Electric Light + Dark Electric override onAccent
          // to a dark value so their light/grey accent buttons stay
          // readable.
          color: disabled ? 'var(--evi-text-mute)' : 'var(--evi-on-accent, #FFFFFF)',
          border: 'none',
          fontFamily: 'inherit',
          fontSize: 15,
          fontWeight: 600,
          letterSpacing: 0.3,
          cursor: disabled ? 'not-allowed' : 'pointer',
          transition: 'transform 0.1s var(--evi-motion-press, ease), opacity 0.2s',
        }}
      >
        {children}
      </button>
    </div>
  );
}
